/**
 * Settings > My-School data layer beyond Grade Ranges (Plan 13).
 *
 * Exposes TanStack Query hooks for:
 *   - Menu slot tags    — list / catalog / upsert / remove / reorder
 *   - Order defaults    — list / upsert / remove
 *   - School change requests   — create (Plan 27 BlockingTask)
 *
 * Server REST routes live at `/api/location-settings/*` (see
 * `apps/ordo/server/src/routes/location-settings.ts`). The single
 * source of truth for these writes is `LocationSettingsManager`.
 */

import type { CanonicalMilkOptionCode } from '@ordo/constants/milk-options'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Mealtime } from '@/lib/mealtime'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — kept identical to the server response shapes so component
// code reads off the API module without re-mapping.
// ---------------------------------------------------------------------------

export type ComponentKind =
  | 'MAIN_ENTREE'
  | 'SIDE'
  | 'FRUIT'
  | 'VEGETABLE'
  | 'GRAIN'
  | 'BEVERAGE'
  | 'CONDIMENT'
  | 'SUPPLEMENT'

export const COMPONENT_KINDS: ReadonlyArray<ComponentKind> = [
  'MAIN_ENTREE',
  'SIDE',
  'FRUIT',
  'VEGETABLE',
  'GRAIN',
  'BEVERAGE',
  'CONDIMENT',
  'SUPPLEMENT',
]

/**
 * Kinds HIDDEN from the percentage-ordering surfaces (the order-sheet
 * component-split editor and the Settings "Pre-fill settings" grid).
 * SUPPLEMENT is garnish/compliance-only and SIDE is a generic bucket
 * that credits nothing — schools don't provision either by %. Hidden
 * kinds implicitly ship at 100%; the API still accepts every
 * ComponentKind on the wire.
 */
export const HIDDEN_SPLIT_KINDS: ReadonlySet<string> = new Set<ComponentKind>(['SIDE', 'SUPPLEMENT'])

/** The kinds the percentage-ordering surfaces show, in display order. */
export const SPLIT_COMPONENT_KINDS: ReadonlyArray<ComponentKind> = COMPONENT_KINDS.filter(
  (kind) => !HIDDEN_SPLIT_KINDS.has(kind),
)

export type SchoolChangeRequestUrgency = 'ROUTINE' | 'TIME_SENSITIVE' | 'URGENT'

export interface MenuSlotTagCatalogEntry {
  id: string
  name: string
  slug: string
  color: string | null
  icon: string | null
}

export interface LocationMenuSlot {
  id: string
  locationId: string
  tagId: string
  sortOrder: number
  tag: MenuSlotTagCatalogEntry
}

/**
 * Enrollment-group dimensions a rate row can be scoped to. AGE_GROUP
 * values carry the LocationGradeRange column id; every other dimension
 * carries the campus vocabulary value label.
 */
export type RateGroupDimension = 'AGE_GROUP' | 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD'

/**
 * Which tier of the settings cascade a row belongs to. Scope is the OUTERMOST
 * resolution axis: a campus row beats a district row even when the district
 * row is more specific on the mealtime axis. See
 * `packages/schema/src/managers/rate-cascade.ts`.
 */
export type RateScopeType = 'ORGANIZATION' | 'LOCATION' | 'LOCATION_WEEKDAY' | 'LOCATION_DAY'

/** Monday-first, matching the settings UI's column order. */
export type Weekday = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'

export const SCHOOL_WEEKDAYS: ReadonlyArray<Weekday> = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
}

/**
 * A campus's recurring weekday rules. Empty = the campus has none, so the
 * "Configure by day" toggle opens OFF for it.
 */
export interface WeekdayRuleSummary {
  locationId: string
  weekdays: Array<{ weekday: Weekday; rateCells: number; milkCells: number }>
  totalCells: number
}

export async function fetchWeekdayRules(locationId: string, signal?: AbortSignal): Promise<WeekdayRuleSummary> {
  return apiFetch<WeekdayRuleSummary>(`/api/location-settings/${encodeURIComponent(locationId)}/weekday-rules`, {
    signal,
  })
}

export const weekdayRulesQueryKey = (locationId: string) => ['location-settings', 'weekday-rules', locationId] as const

export function useWeekdayRulesQuery(locationId: string | null) {
  return useQuery<WeekdayRuleSummary>({
    queryKey: weekdayRulesQueryKey(locationId ?? ''),
    queryFn: ({ signal }) => fetchWeekdayRules(locationId!, signal),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

/** A resolved percentage and the tier that authored it. `source: null` = platform 100%. */
export interface ResolvedRateCell {
  bps: number
  source: RateScopeType | null
}

/** Cascading OVS split opt-out. `null` at a tier = inherit. */
export type ComponentSplitMode = 'ENABLED' | 'DISABLED'

export interface LocationComponentDeliveryRate {
  id: string
  locationId: string
  /** The tier that OWNS this row. */
  scopeType?: RateScopeType
  scopeId?: string
  /** Non-null only on a `LOCATION_WEEKDAY` row — the weekday it recurs on. */
  weekday?: Weekday | null
  componentKind: ComponentKind
  deliveryRateBps: number
  /** Enrollment-group scope; null/null = location-wide. */
  groupDimension: RateGroupDimension | null
  groupValue: string | null
  /** Mealtime scope; null = legacy row applying to every mealtime. */
  mealtime: Mealtime | null
}

export interface SchoolChangeRequestTask {
  id: string
  title: string
  status: string
  dueAt: string | null
  assignedToProfileCode: string | null
  blocksSubjectKind: string
  blocksSubjectId: string | null
}

// ---------------------------------------------------------------------------
// Pure helpers — exported so they can be unit-tested in isolation.
// ---------------------------------------------------------------------------

// The percent ⇄ basis-point conversions live with the draft model in
// `@/lib/school-orders/split-draft` (pure, no react-query) and are re-exported
// here so every existing `@/api/location-settings` import keeps working.
export {
  bpsToPercentString,
  cellDisplayValue,
  clearedCellValue,
  draftActionFor,
  effectiveCellBps,
  isDraftValueInvalid,
  percentStringToBps,
  type SplitDraftAction,
} from '@/lib/school-orders/split-draft'

// Percentage-surface display labels. BEVERAGE reads "Milk" — public-school
// OVS beverages are exclusively milk (juice is categorized under FRUIT) —
// and FRUIT reads "Fruit/Juice" since juice credits as fruit.
const COMPONENT_KIND_LABELS: Record<ComponentKind, string> = {
  MAIN_ENTREE: 'Main entrée',
  SIDE: 'Side',
  FRUIT: 'Fruit/Juice',
  VEGETABLE: 'Vegetable',
  GRAIN: 'Grain',
  BEVERAGE: 'Milk',
  CONDIMENT: 'Condiment',
  SUPPLEMENT: 'Supplement',
}

export function componentKindLabel(kind: ComponentKind): string {
  return COMPONENT_KIND_LABELS[kind]
}

// ---------------------------------------------------------------------------
// Menu slots
// ---------------------------------------------------------------------------

export interface MenuSlotsResponse {
  slots: LocationMenuSlot[]
}

export interface MenuSlotTagCatalogResponse {
  tags: MenuSlotTagCatalogEntry[]
}

export async function fetchMenuSlots(locationId: string, signal?: AbortSignal): Promise<MenuSlotsResponse> {
  return apiFetch<MenuSlotsResponse>(`/api/location-settings/${encodeURIComponent(locationId)}/menu-slots`, { signal })
}

export async function fetchMenuSlotTagCatalog(signal?: AbortSignal): Promise<MenuSlotTagCatalogResponse> {
  return apiFetch<MenuSlotTagCatalogResponse>('/api/location-settings/menu-slot-tags', { signal })
}

export interface UpsertMenuSlotVariables {
  locationId: string
  tagId: string
  sortOrder?: number | null
}

export async function upsertMenuSlot(vars: UpsertMenuSlotVariables): Promise<LocationMenuSlot> {
  return apiFetch<LocationMenuSlot>(`/api/location-settings/${encodeURIComponent(vars.locationId)}/menu-slots`, {
    method: 'POST',
    body: { tagId: vars.tagId, sortOrder: vars.sortOrder ?? null },
  })
}

export async function removeMenuSlot(id: string): Promise<{ id: string; isArchived: boolean }> {
  return apiFetch<{ id: string; isArchived: boolean }>(`/api/location-settings/menu-slots/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export interface ReorderMenuSlotsVariables {
  locationId: string
  orderedIds: string[]
}

export async function reorderMenuSlots(
  vars: ReorderMenuSlotsVariables,
): Promise<{ slots: Array<{ id: string; sortOrder: number; tagId: string }> }> {
  return apiFetch(`/api/location-settings/${encodeURIComponent(vars.locationId)}/menu-slots/reorder`, {
    method: 'PUT',
    body: { orderedIds: vars.orderedIds },
  })
}

export const menuSlotsQueryKey = (locationId: string) => ['location-settings', 'menu-slots', locationId] as const
export const menuSlotTagCatalogQueryKey = ['location-settings', 'menu-slot-tags'] as const

export function useMenuSlotsQuery(locationId: string | null) {
  return useQuery<MenuSlotsResponse>({
    queryKey: menuSlotsQueryKey(locationId ?? ''),
    queryFn: ({ signal }) => fetchMenuSlots(locationId!, signal),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

export function useMenuSlotTagCatalogQuery() {
  return useQuery<MenuSlotTagCatalogResponse>({
    queryKey: menuSlotTagCatalogQueryKey,
    queryFn: ({ signal }) => fetchMenuSlotTagCatalog(signal),
    staleTime: 5 * 60_000,
  })
}

export function useUpsertMenuSlotMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: upsertMenuSlot,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: menuSlotsQueryKey(vars.locationId) })
    },
  })
}

export function useRemoveMenuSlotMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: removeMenuSlot,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: menuSlotsQueryKey(locationId) })
    },
  })
}

export function useReorderMenuSlotsMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: reorderMenuSlots,
    onMutate: async (vars) => {
      // Optimistic reorder — flip the cached list so the UI doesn't
      // wait on the server round-trip.
      await qc.cancelQueries({ queryKey: menuSlotsQueryKey(vars.locationId) })
      const previous = qc.getQueryData<MenuSlotsResponse>(menuSlotsQueryKey(vars.locationId))
      qc.setQueryData<MenuSlotsResponse | undefined>(menuSlotsQueryKey(vars.locationId), (prev) => {
        if (!prev) {
          return prev
        }
        const byId = new Map(prev.slots.map((s) => [s.id, s]))
        const reordered: LocationMenuSlot[] = []
        for (const id of vars.orderedIds) {
          const slot = byId.get(id)
          if (slot) {
            reordered.push({ ...slot, sortOrder: (reordered.length + 1) * 10 })
          }
        }
        return { slots: reordered }
      })
      return { previous }
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(menuSlotsQueryKey(vars.locationId), ctx.previous)
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: menuSlotsQueryKey(vars.locationId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Order defaults. The API path/type names stay legacy-compatible while the
// UI describes these as order defaults.
// ---------------------------------------------------------------------------

export interface ComponentDeliveryRatesResponse {
  /** Which tier `rates` belongs to. */
  scopeType: RateScopeType
  scopeId: string
  organizationId: string
  /** Rows this tier OWNS. Never includes inherited rows. */
  rates: LocationComponentDeliveryRate[]
  /**
   * The DISTRICT's own rows, when reading the campus tier. Rendered as input
   * PLACEHOLDERS, never as values.
   *
   * ⚠ The load-bearing invariant: **a displayed inherited value is never
   * persisted.** Round-tripping the district's 50% into a stored campus row
   * detaches the campus permanently, and a later district edit silently stops
   * reaching it — a strictly worse version of the bug this cascade fixes.
   */
  inheritedRates: LocationComponentDeliveryRate[]
  componentKinds: ComponentKind[]
}

export async function fetchComponentDeliveryRates(
  locationId: string,
  signal?: AbortSignal,
  scope?: RateScopeType,
): Promise<ComponentDeliveryRatesResponse> {
  const query = scope === 'ORGANIZATION' ? '?scope=ORGANIZATION' : ''
  return apiFetch<ComponentDeliveryRatesResponse>(
    `/api/location-settings/${encodeURIComponent(locationId)}/delivery-rates${query}`,
    { signal },
  )
}

export interface ResolvedDeliveryRatesResponse {
  locationId: string
  mealtime: Mealtime | null
  /**
   * componentKind → resolved value + provenance. A kind that is ABSENT was
   * configured by nobody (the platform 100%); a kind present with
   * `bps: 10000` was explicitly set to 100% by whoever `source` names. The
   * difference matters — an explicit 100% overrides the district.
   */
  cells: Partial<Record<ComponentKind, ResolvedRateCell>>
}

export async function fetchResolvedDeliveryRates(
  locationId: string,
  mealtime: Mealtime | null,
  signal?: AbortSignal,
): Promise<ResolvedDeliveryRatesResponse> {
  const query = mealtime ? `?mealtime=${encodeURIComponent(mealtime)}` : ''
  return apiFetch<ResolvedDeliveryRatesResponse>(
    `/api/location-settings/${encodeURIComponent(locationId)}/delivery-rates/resolved${query}`,
    { signal },
  )
}

export interface UpsertComponentDeliveryRateVariables {
  locationId: string
  /** Required when `scopeType` is `'LOCATION_WEEKDAY'`. */
  weekday?: Weekday | null
  /** Which tier to write. Omitted = the campus's own tier. */
  scopeType?: RateScopeType
  /** Required (the Organization id) when `scopeType` is `'ORGANIZATION'`. */
  scopeId?: string
  componentKind: ComponentKind
  deliveryRateBps: number
  /** Scope the rate to one enrollment group; both omitted = location-wide. */
  groupDimension?: RateGroupDimension | null
  groupValue?: string | null
  /** Scope the rate to one mealtime; omitted = the legacy all-mealtimes row. */
  mealtime?: Mealtime | null
}

export async function upsertComponentDeliveryRate(
  vars: UpsertComponentDeliveryRateVariables,
): Promise<LocationComponentDeliveryRate> {
  return apiFetch<LocationComponentDeliveryRate>(
    `/api/location-settings/${encodeURIComponent(vars.locationId)}/delivery-rates`,
    {
      method: 'PUT',
      body: {
        scopeType: vars.scopeType ?? 'LOCATION',
        scopeId: vars.scopeId,
        weekday: vars.weekday ?? null,
        componentKind: vars.componentKind,
        deliveryRateBps: vars.deliveryRateBps,
        groupDimension: vars.groupDimension ?? null,
        groupValue: vars.groupValue ?? null,
        mealtime: vars.mealtime ?? null,
      },
    },
  )
}

export async function removeComponentDeliveryRate(id: string): Promise<{ id: string; isArchived: boolean }> {
  return apiFetch<{ id: string; isArchived: boolean }>(
    `/api/location-settings/delivery-rates/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
}

/** Clear one rate cell back to INHERITED. */
export interface ClearComponentDeliveryRateVariables {
  locationId: string
  /** Required when `scopeType` is `'LOCATION_WEEKDAY'`. */
  weekday?: Weekday | null
  scopeType?: RateScopeType
  scopeId?: string
  componentKind: ComponentKind
  groupDimension?: RateGroupDimension | null
  groupValue?: string | null
  mealtime?: Mealtime | null
}

/**
 * Archive this tier's row for the cell so it inherits again. `cleared: false`
 * means the cell was ALREADY inheriting — a successful no-op, not an error.
 * There is no sentinel value for "inherit": inherit IS the absence of a row.
 */
export async function clearComponentDeliveryRate(
  vars: ClearComponentDeliveryRateVariables,
): Promise<{ cleared: boolean; id: string | null }> {
  return apiFetch<{ cleared: boolean; id: string | null }>(
    `/api/location-settings/${encodeURIComponent(vars.locationId)}/delivery-rates/clear`,
    {
      method: 'POST',
      body: {
        scopeType: vars.scopeType ?? 'LOCATION',
        scopeId: vars.scopeId,
        weekday: vars.weekday ?? null,
        componentKind: vars.componentKind,
        groupDimension: vars.groupDimension ?? null,
        groupValue: vars.groupValue ?? null,
        mealtime: vars.mealtime ?? null,
      },
    },
  )
}

/**
 * Wholesale removal of a campus's stored split overrides, so it inherits the
 * district again.
 *
 * `CAMPUS` clears everything this campus owns, its recurring weekday rules
 * included; `WEEKDAY` clears only the day rules. Covers BOTH the delivery-rate
 * rows and the milk-share rows — a campus left inheriting the district's
 * vegetable share while still overriding its milk types is half-detached, and
 * nothing in the UI would say so.
 *
 * ⚠ Irreversible from the UI's point of view (the rows are soft-deleted, but
 * nothing re-attaches them). Callers MUST confirm with the user first, naming
 * the number of overrides about to go.
 */
export type CampusSplitOverrideLayer = 'CAMPUS' | 'WEEKDAY'

export interface ClearCampusSplitOverridesVariables {
  locationId: string
  layer: CampusSplitOverrideLayer
}

export interface ClearedCampusSplitOverrides {
  locationId: string
  layer: CampusSplitOverrideLayer
  rateCells: number
  milkCells: number
}

export async function clearCampusSplitOverrides(
  vars: ClearCampusSplitOverridesVariables,
): Promise<ClearedCampusSplitOverrides> {
  return apiFetch<ClearedCampusSplitOverrides>(
    `/api/location-settings/${encodeURIComponent(vars.locationId)}/split-overrides/clear`,
    { method: 'POST', body: { layer: vars.layer } },
  )
}

export function useClearCampusSplitOverridesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: clearCampusSplitOverrides,
    onSuccess: (_data, vars) => {
      invalidateSplitSettings(qc, vars.locationId)
    },
  })
}

export const deliveryRatesQueryKey = (locationId: string, scope: RateScopeType = 'LOCATION') =>
  ['location-settings', 'delivery-rates', locationId, scope] as const

export const resolvedDeliveryRatesQueryKey = (locationId: string, mealtime: Mealtime | null) =>
  ['location-settings', 'delivery-rates', 'resolved', locationId, mealtime ?? 'all'] as const

export function useComponentDeliveryRatesQuery(locationId: string | null, scope: RateScopeType = 'LOCATION') {
  return useQuery<ComponentDeliveryRatesResponse>({
    queryKey: deliveryRatesQueryKey(locationId ?? '', scope),
    queryFn: ({ signal }) => fetchComponentDeliveryRates(locationId!, signal, scope),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

export function useResolvedDeliveryRatesQuery(locationId: string | null, mealtime: Mealtime | null) {
  return useQuery<ResolvedDeliveryRatesResponse>({
    queryKey: resolvedDeliveryRatesQueryKey(locationId ?? '', mealtime),
    queryFn: ({ signal }) => fetchResolvedDeliveryRates(locationId!, mealtime, signal),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

/**
 * A rate write at ANY tier changes what a campus resolves, so invalidate both
 * tiers' lists and every resolved view — a district edit that left the campus
 * grid showing stale numbers would recreate the exact confusion this work
 * removes.
 */
function invalidateSplitSettings(qc: ReturnType<typeof useQueryClient>, locationId: string) {
  void qc.invalidateQueries({ queryKey: ['location-settings', 'delivery-rates'] })
  void qc.invalidateQueries({ queryKey: ['location-settings', 'milk-shares'] })
  void qc.invalidateQueries({ queryKey: ['location-settings', 'district-split'] })
  void qc.invalidateQueries({ queryKey: ['location-settings', 'weekday-rules'] })
  void qc.invalidateQueries({ queryKey: orderPreferencesQueryKey(locationId) })
  // Unplaced orders must pick the new percentages up IMMEDIATELY. The order
  // sheet derives every untyped component cell live from `ratesFor` (see
  // `day-order-panel.tsx` → `rateBasisFor` / `resolveComponentQuantities`), so
  // the rate-list invalidation above is what re-derives a day still in draft.
  // These two carry the same numbers on the summary/forecast surfaces, and a
  // sheet that still showed the old split after a save is exactly the "it
  // didn't save" report this work exists to end.
  void qc.invalidateQueries({ queryKey: ['school-orders', 'service-day'] })
  void qc.invalidateQueries({ queryKey: ['school-orders', 'service-summary'] })
}

/**
 * The same invalidation, for callers that write rates through the bare
 * `upsert*`/`clear*` functions rather than the per-cell mutation hooks — the
 * Save button batches a dialog's worth of writes and invalidates ONCE at the
 * end, instead of kicking off a refetch storm per cell.
 */
export function useInvalidateSplitSettings() {
  const qc = useQueryClient()
  return (locationId: string) => {
    invalidateSplitSettings(qc, locationId)
  }
}

export function useUpsertComponentDeliveryRateMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: upsertComponentDeliveryRate,
    onSuccess: (_data, vars) => {
      invalidateSplitSettings(qc, vars.locationId)
    },
  })
}

export function useClearComponentDeliveryRateMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: clearComponentDeliveryRate,
    onSuccess: (_data, vars) => {
      invalidateSplitSettings(qc, vars.locationId)
    },
  })
}

export function useRemoveComponentDeliveryRateMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: removeComponentDeliveryRate,
    onSuccess: () => {
      invalidateSplitSettings(qc, locationId)
    },
  })
}

// ---------------------------------------------------------------------------
// Milk shares — the nested per-SKU layer under the Milk (BEVERAGE) kind.
// The school's linked milk SKUs come from its BEVERAGE-category component
// group preferences; shares are % of the resolved MILK TOTAL (not the
// meal count). Missing rows render as the muted even-split default.
// ---------------------------------------------------------------------------

export interface MilkSku {
  id: string
  name: string
  /** Canonical milk-type stamp (`Component.milkOptionCode`); null = unstamped group-pref SKU. */
  milkOptionCode: string | null
  /** Explicit group-preference link, resolved milk-lineup type, or both. */
  source: 'GROUP_PREF' | 'LINEUP' | 'BOTH'
}

export interface LocationMilkShareRate {
  id: string
  locationId: string
  /** The milk SKU (Component id) this share belongs to. */
  componentId: string
  /** Basis points (0-10000) of the resolved milk total. */
  shareBps: number
  /** Enrollment-group scope; null/null = location-wide. */
  groupDimension: RateGroupDimension | null
  groupValue: string | null
  /** Mealtime scope; null = legacy row applying to every mealtime. */
  mealtime: Mealtime | null
}

/**
 * The school-level milk profile — the district's chosen milk TYPES + the
 * explicit "no milk" flag. Read-only source-of-truth context for the milk-share
 * editor; keyed by MENU_MILK_OPTION_CODE, not by SKU/component id.
 */
export interface MilkProfile {
  milkOptionCodes: CanonicalMilkOptionCode[]
  noMilkMode: boolean
}

export interface MilkSharesResponse {
  scopeType: RateScopeType
  scopeId: string
  organizationId: string
  milkSkus: MilkSku[]
  /** Rows this tier OWNS. */
  shares: LocationMilkShareRate[]
  /**
   * The DISTRICT's own share rows, when reading the campus tier.
   *
   * ⚠ Milk shares resolve as a WHOLE SET per tier, so a campus that stores
   * ANY share row for a cell owns the ENTIRE split for that cell. The editor
   * must therefore write the COMPLETE set when the user first overrides, and
   * clear the COMPLETE set to go back — otherwise a campus that nudges one
   * milk type resolves the district's other types to an implicit 0.
   */
  inheritedShares: LocationMilkShareRate[]
  milkProfile: MilkProfile
}

export async function fetchMilkShares(
  locationId: string,
  signal?: AbortSignal,
  scope?: RateScopeType,
): Promise<MilkSharesResponse> {
  const query = scope === 'ORGANIZATION' ? '?scope=ORGANIZATION' : ''
  return apiFetch<MilkSharesResponse>(`/api/location-settings/${encodeURIComponent(locationId)}/milk-shares${query}`, {
    signal,
  })
}

export interface UpsertMilkShareRateVariables {
  locationId: string
  /** Required when `scopeType` is `'LOCATION_WEEKDAY'`. */
  weekday?: Weekday | null
  scopeType?: RateScopeType
  scopeId?: string
  componentId: string
  shareBps: number
  /** Scope the share to one enrollment group; both omitted = location-wide. */
  groupDimension?: RateGroupDimension | null
  groupValue?: string | null
  /** Scope the share to one mealtime; omitted = the legacy all-mealtimes row. */
  mealtime?: Mealtime | null
}

export async function upsertMilkShareRate(vars: UpsertMilkShareRateVariables): Promise<LocationMilkShareRate> {
  return apiFetch<LocationMilkShareRate>(`/api/location-settings/${encodeURIComponent(vars.locationId)}/milk-shares`, {
    method: 'PUT',
    body: {
      scopeType: vars.scopeType ?? 'LOCATION',
      scopeId: vars.scopeId,
      weekday: vars.weekday ?? null,
      componentId: vars.componentId,
      shareBps: vars.shareBps,
      groupDimension: vars.groupDimension ?? null,
      groupValue: vars.groupValue ?? null,
      mealtime: vars.mealtime ?? null,
    },
  })
}

export async function removeMilkShareRate(id: string): Promise<{ id: string; isArchived: boolean }> {
  return apiFetch<{ id: string; isArchived: boolean }>(`/api/location-settings/milk-shares/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/**
 * Clear the WHOLE milk-share set for one cell back to inherited. Returns the
 * number of rows archived (0 = the cell was already inheriting).
 */
export interface ClearMilkShareSetVariables {
  locationId: string
  /** Required when `scopeType` is `'LOCATION_WEEKDAY'`. */
  weekday?: Weekday | null
  scopeType?: RateScopeType
  scopeId?: string
  groupDimension?: RateGroupDimension | null
  groupValue?: string | null
  mealtime?: Mealtime | null
}

export async function clearMilkShareSet(vars: ClearMilkShareSetVariables): Promise<{ cleared: number }> {
  return apiFetch<{ cleared: number }>(
    `/api/location-settings/${encodeURIComponent(vars.locationId)}/milk-shares/clear`,
    {
      method: 'POST',
      body: {
        scopeType: vars.scopeType ?? 'LOCATION',
        scopeId: vars.scopeId,
        weekday: vars.weekday ?? null,
        groupDimension: vars.groupDimension ?? null,
        groupValue: vars.groupValue ?? null,
        mealtime: vars.mealtime ?? null,
      },
    },
  )
}

export const milkSharesQueryKey = (locationId: string, scope: RateScopeType = 'LOCATION') =>
  ['location-settings', 'milk-shares', locationId, scope] as const

export function useMilkSharesQuery(locationId: string | null, scope: RateScopeType = 'LOCATION') {
  return useQuery<MilkSharesResponse>({
    queryKey: milkSharesQueryKey(locationId ?? '', scope),
    queryFn: ({ signal }) => fetchMilkShares(locationId!, signal, scope),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

export function useUpsertMilkShareRateMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: upsertMilkShareRate,
    onSuccess: (_data, vars) => {
      invalidateSplitSettings(qc, vars.locationId)
    },
  })
}

export function useClearMilkShareSetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: clearMilkShareSet,
    onSuccess: (_data, vars) => {
      invalidateSplitSettings(qc, vars.locationId)
    },
  })
}

export function useRemoveMilkShareRateMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: removeMilkShareRate,
    onSuccess: () => {
      invalidateSplitSettings(qc, locationId)
    },
  })
}

// ---------------------------------------------------------------------------
// Order-sheet prefill preferences (v2 "Pre-fill settings")
// ---------------------------------------------------------------------------

/**
 * A campus's resolved split state with provenance — the data the UI needs to
 * say WHY the split is off, which is the single thing the pre-cascade surface
 * never said. An admin edited district percentages believing they applied
 * everywhere; two of three campuses had the split off and shipped every
 * component at 100%, including 1,526 milk cartons removed by hand.
 */
export interface ComponentSplitStatus {
  /** This campus's own stored mode. `null` = inheriting the district. */
  locationMode: ComponentSplitMode | null
  /** The district's stored mode. `null` = no district opt-out. */
  organizationMode: ComponentSplitMode | null
  /** The resolved mode. `null` = nothing opted out anywhere. */
  mode: ComponentSplitMode | null
  /** Which tier decided `mode` — "off at this campus" vs "off for the district". */
  source: 'LOCATION' | 'ORGANIZATION' | null
  /** `false` only for a resolved `DISABLED`: this campus ships 100%. */
  allowed: boolean
}

export interface OrderPreferences {
  /** DEPRECATED — derived from `componentSplit.allowed`. Read that instead. */
  prefillComponentSplitEnabled: boolean
  componentSplit: ComponentSplitStatus
  prefillMealSelectionEnabled: boolean
  /** The campus auto-order switch — the column the SCHOOL_FORECAST fill gate reads. */
  autoOrderEnabled: boolean
}

/** The PATCH body: `componentSplitMode: null` clears the campus override. */
export interface UpdateOrderPreferencesVariables {
  locationId: string
  componentSplitMode?: ComponentSplitMode | null
  prefillMealSelectionEnabled?: boolean
  autoOrderEnabled?: boolean
}

export async function fetchOrderPreferences(locationId: string, signal?: AbortSignal): Promise<OrderPreferences> {
  return apiFetch<OrderPreferences>(`/api/location-settings/${encodeURIComponent(locationId)}/order-preferences`, {
    signal,
  })
}

export async function updateOrderPreferences(vars: UpdateOrderPreferencesVariables): Promise<OrderPreferences> {
  const { locationId, ...body } = vars
  return apiFetch<OrderPreferences>(`/api/location-settings/${encodeURIComponent(locationId)}/order-preferences`, {
    method: 'PATCH',
    body,
  })
}

export const orderPreferencesQueryKey = (locationId: string) =>
  ['location-settings', 'order-preferences', locationId] as const

export function useOrderPreferencesQuery(locationId: string | null) {
  return useQuery<OrderPreferences>({
    queryKey: orderPreferencesQueryKey(locationId ?? ''),
    queryFn: ({ signal }) => fetchOrderPreferences(locationId!, signal),
    enabled: !!locationId,
    staleTime: 30_000,
  })
}

export function useUpdateOrderPreferencesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateOrderPreferences,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: orderPreferencesQueryKey(vars.locationId) })
      void qc.invalidateQueries({ queryKey: ['location-settings', 'district-split'] })
      // The order sheet reads the flags off /my-schools too.
      void qc.invalidateQueries({ queryKey: ['school-orders', 'my-schools'] })
    },
  })
}

// ---------------------------------------------------------------------------
// The district split matrix — every campus's effective settings at a glance.
// ---------------------------------------------------------------------------

export interface DistrictSplitCampus {
  locationId: string
  locationName: string
  /** `false` = this campus ships 100% regardless of the numbers. */
  splitAllowed: boolean
  splitMode: ComponentSplitMode | null
  splitSource: 'LOCATION' | 'ORGANIZATION' | null
  /**
   * How many per-order-column rows this campus owns for this mealtime.
   * Non-zero means the single number shown is not the whole story and the
   * reader should open the campus grid.
   */
  columnOverrideCount: number
  /**
   * Weekdays this campus has recurring rules for. Non-empty means the numbers
   * in this row do not hold on those days.
   */
  weekdaysWithRules: Weekday[]
  cells: Record<string, ResolvedRateCell>
}

export interface DistrictSplitMealtime {
  mealtime: Mealtime
  /** The district's own rows for this mealtime, keyed by componentKind. */
  districtDefaults: Record<string, number>
  campuses: DistrictSplitCampus[]
}

export interface DistrictSplitMatrix {
  organizationId: string
  organizationName: string
  organizationSplitMode: ComponentSplitMode | null
  mealtimes: DistrictSplitMealtime[]
}

export async function fetchDistrictSplitMatrix(
  organizationId: string,
  mealtimes: readonly Mealtime[],
  signal?: AbortSignal,
): Promise<DistrictSplitMatrix> {
  const query = mealtimes.length > 0 ? `?mealtimes=${encodeURIComponent(mealtimes.join(','))}` : ''
  return apiFetch<DistrictSplitMatrix>(
    `/api/location-settings/organizations/${encodeURIComponent(organizationId)}/split-matrix${query}`,
    { signal },
  )
}

export const districtSplitMatrixQueryKey = (organizationId: string, mealtimes: readonly Mealtime[]) =>
  ['location-settings', 'district-split', organizationId, [...mealtimes].sort().join(',')] as const

export function useDistrictSplitMatrixQuery(organizationId: string | null, mealtimes: readonly Mealtime[]) {
  return useQuery<DistrictSplitMatrix>({
    queryKey: districtSplitMatrixQueryKey(organizationId ?? '', mealtimes),
    queryFn: ({ signal }) => fetchDistrictSplitMatrix(organizationId!, mealtimes, signal),
    enabled: !!organizationId && mealtimes.length > 0,
    staleTime: 30_000,
  })
}

/** Set or clear the DISTRICT-wide split opt-out. `null` clears it. */
export async function updateOrganizationSplitMode(vars: {
  organizationId: string
  componentSplitMode: ComponentSplitMode | null
}): Promise<{ componentSplitMode: ComponentSplitMode | null }> {
  return apiFetch<{ componentSplitMode: ComponentSplitMode | null }>(
    `/api/location-settings/organizations/${encodeURIComponent(vars.organizationId)}/split-mode`,
    { method: 'PATCH', body: { componentSplitMode: vars.componentSplitMode } },
  )
}

export function useUpdateOrganizationSplitModeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateOrganizationSplitMode,
    onSuccess: () => {
      // Clearing a district opt-out makes every inheriting campus start
      // applying whatever percentages resolve, so every campus's view is stale.
      void qc.invalidateQueries({ queryKey: ['location-settings'] })
      void qc.invalidateQueries({ queryKey: ['school-orders', 'my-schools'] })
    },
  })
}

// ---------------------------------------------------------------------------
// School change requests (Plan 27 BlockingTask)
// ---------------------------------------------------------------------------

export interface CreateSchoolChangeRequestVariables {
  locationId: string
  field: string
  currentValue: string | null
  proposedValue: string
  reason: string
  urgency: SchoolChangeRequestUrgency
}

export async function createSchoolChangeRequest(
  vars: CreateSchoolChangeRequestVariables,
): Promise<SchoolChangeRequestTask> {
  const { locationId, ...body } = vars
  return apiFetch<SchoolChangeRequestTask>(`/api/location-settings/${encodeURIComponent(locationId)}/change-requests`, {
    method: 'POST',
    body,
  })
}

export function useCreateSchoolChangeRequestMutation() {
  return useMutation({
    mutationFn: createSchoolChangeRequest,
  })
}
